import { supabase } from './supabase';
import type { HAConfig, HAEntity, HAEntityState } from '../types';

// ============================================
// CONFIGURATION
// ============================================

export async function getHAConfig(householdId: string): Promise<HAConfig | null> {
  const { data } = await supabase
    .from('ha_config')
    .select('*')
    .eq('household_id', householdId)
    .single();

  return data;
}

export async function saveHAConfig(
  householdId: string,
  baseUrl: string,
  accessToken: string
): Promise<HAConfig | null> {
  // Validate connection first
  const isValid = await testHAConnection(baseUrl, accessToken);
  if (!isValid) return null;

  const { data, error } = await supabase
    .from('ha_config')
    .upsert({
      household_id: householdId,
      base_url: baseUrl.replace(/\/$/, ''), // Remove trailing slash
      access_token: accessToken,
      is_enabled: true,
      last_connected_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to save HA config:', error);
    return null;
  }

  return data;
}

export async function testHAConnection(baseUrl: string, accessToken: string): Promise<boolean> {
  try {
    const response = await fetch(`${baseUrl}/api/`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    return response.ok;
  } catch {
    return false;
  }
}

// ============================================
// ENTITY STATE
// ============================================

export async function fetchEntityState(
  config: HAConfig,
  entityId: string
): Promise<HAEntityState | null> {
  try {
    const response = await fetch(`${config.base_url}/api/states/${entityId}`, {
      headers: {
        Authorization: `Bearer ${config.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) return null;

    return response.json();
  } catch {
    return null;
  }
}

export async function fetchAllEntityStates(
  config: HAConfig
): Promise<HAEntityState[]> {
  try {
    const response = await fetch(`${config.base_url}/api/states`, {
      headers: {
        Authorization: `Bearer ${config.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) return [];

    return response.json();
  } catch {
    return [];
  }
}

// ============================================
// ENTITY CONTROL
// ============================================

export async function callHAService(
  config: HAConfig,
  domain: string,
  service: string,
  entityId?: string,
  data?: Record<string, unknown>
): Promise<boolean> {
  try {
    const response = await fetch(`${config.base_url}/api/services/${domain}/${service}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${config.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entity_id: entityId,
        ...data,
      }),
    });

    return response.ok;
  } catch {
    return false;
  }
}

export async function toggleEntity(config: HAConfig, entityId: string): Promise<boolean> {
  const domain = entityId.split('.')[0];
  
  // Determine the appropriate service based on domain
  let service = 'toggle';
  if (domain === 'cover') {
    // Covers don't have toggle, need to check state
    const state = await fetchEntityState(config, entityId);
    service = state?.state === 'open' ? 'close_cover' : 'open_cover';
  } else if (domain === 'lock') {
    const state = await fetchEntityState(config, entityId);
    service = state?.state === 'locked' ? 'unlock' : 'lock';
  }

  return callHAService(config, domain, service, entityId);
}

export async function turnOn(config: HAConfig, entityId: string): Promise<boolean> {
  const domain = entityId.split('.')[0];
  return callHAService(config, domain, 'turn_on', entityId);
}

export async function turnOff(config: HAConfig, entityId: string): Promise<boolean> {
  const domain = entityId.split('.')[0];
  return callHAService(config, domain, 'turn_off', entityId);
}

export async function setBrightness(
  config: HAConfig,
  entityId: string,
  brightness: number
): Promise<boolean> {
  return callHAService(config, 'light', 'turn_on', entityId, {
    brightness: Math.round((brightness / 100) * 255),
  });
}

export async function setTemperature(
  config: HAConfig,
  entityId: string,
  temperature: number
): Promise<boolean> {
  return callHAService(config, 'climate', 'set_temperature', entityId, {
    temperature,
  });
}

// ============================================
// ENTITY DISCOVERY
// ============================================

const SUPPORTED_DOMAINS = [
  'light',
  'switch',
  'cover',
  'lock',
  'climate',
  'fan',
  'media_player',
  'scene',
  'script',
  'automation',
  'input_boolean',
  'vacuum',
  'camera',
];

export async function discoverEntities(config: HAConfig): Promise<HAEntityState[]> {
  const allStates = await fetchAllEntityStates(config);
  
  return allStates.filter(state => {
    const domain = state.entity_id.split('.')[0];
    return SUPPORTED_DOMAINS.includes(domain);
  });
}

export function getEntityIcon(entityId: string, state?: string): string {
  const domain = entityId.split('.')[0];
  const isOn = state === 'on' || state === 'open' || state === 'unlocked';

  const icons: Record<string, string> = {
    light: isOn ? '💡' : '🔅',
    switch: isOn ? '🔌' : '⭕',
    cover: state === 'open' ? '🪟' : '🚪',
    lock: state === 'locked' ? '🔒' : '🔓',
    climate: '🌡️',
    fan: isOn ? '🌀' : '💨',
    media_player: '📺',
    scene: '🎬',
    script: '📜',
    automation: '⚙️',
    input_boolean: isOn ? '✅' : '⬜',
    vacuum: '🧹',
    camera: '📷',
  };

  return icons[domain] || '❓';
}

export function formatEntityState(entityId: string, state: HAEntityState): string {
  const domain = entityId.split('.')[0];

  switch (domain) {
    case 'climate':
      const temp = state.attributes.current_temperature;
      const target = state.attributes.temperature;
      return temp ? `${temp}° → ${target}°` : state.state;
    
    case 'light':
      if (state.state === 'on' && state.attributes.brightness) {
        const percent = Math.round((state.attributes.brightness as number) / 255 * 100);
        return `${percent}%`;
      }
      return state.state;
    
    case 'cover':
      if (state.attributes.current_position !== undefined) {
        return `${state.attributes.current_position}%`;
      }
      return state.state;
    
    case 'media_player':
      if (state.state === 'playing') {
        return state.attributes.media_title as string || 'Playing';
      }
      return state.state;
    
    default:
      return state.state;
  }
}

// ============================================
// MANAGED ENTITIES
// ============================================

export async function getManagedEntities(householdId: string): Promise<HAEntity[]> {
  const { data } = await supabase
    .from('ha_entities')
    .select('*')
    .eq('household_id', householdId)
    .eq('show_on_dashboard', true)
    .order('sort_order');

  return data || [];
}

export async function addManagedEntity(
  householdId: string,
  entityId: string,
  friendlyName?: string
): Promise<HAEntity | null> {
  const { data, error } = await supabase
    .from('ha_entities')
    .insert({
      household_id: householdId,
      entity_id: entityId,
      friendly_name: friendlyName,
      icon: getEntityIcon(entityId),
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to add entity:', error);
    return null;
  }

  return data;
}

export async function removeManagedEntity(entityDbId: string): Promise<boolean> {
  const { error } = await supabase
    .from('ha_entities')
    .delete()
    .eq('id', entityDbId);

  return !error;
}

// ============================================
// WEBSOCKET (for realtime updates)
// ============================================

export class HAWebSocket {
  private ws: WebSocket | null = null;
  private config: HAConfig;
  private messageId = 1;
  private subscribers: Map<string, (state: HAEntityState) => void> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  constructor(config: HAConfig) {
    this.config = config;
  }

  connect(): Promise<boolean> {
    return new Promise((resolve) => {
      const wsUrl = this.config.base_url.replace(/^http/, 'ws') + '/api/websocket';
      
      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('HA WebSocket connected');
        };

        this.ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          this.handleMessage(data, resolve);
        };

        this.ws.onerror = () => {
          console.error('HA WebSocket error');
          resolve(false);
        };

        this.ws.onclose = () => {
          console.log('HA WebSocket closed');
          this.attemptReconnect();
        };
      } catch {
        resolve(false);
      }
    });
  }

  private handleMessage(data: any, resolveConnect?: (value: boolean) => void) {
    switch (data.type) {
      case 'auth_required':
        this.authenticate();
        break;
      
      case 'auth_ok':
        console.log('HA WebSocket authenticated');
        this.subscribeToStateChanges();
        resolveConnect?.(true);
        this.reconnectAttempts = 0;
        break;
      
      case 'auth_invalid':
        console.error('HA WebSocket auth failed');
        resolveConnect?.(false);
        break;
      
      case 'event':
        if (data.event?.event_type === 'state_changed') {
          const newState = data.event.data.new_state;
          const callback = this.subscribers.get(newState.entity_id);
          callback?.(newState);
        }
        break;
    }
  }

  private authenticate() {
    this.send({
      type: 'auth',
      access_token: this.config.access_token,
    });
  }

  private subscribeToStateChanges() {
    this.send({
      id: this.messageId++,
      type: 'subscribe_events',
      event_type: 'state_changed',
    });
  }

  private send(message: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      setTimeout(() => this.connect(), delay);
    }
  }

  subscribe(entityId: string, callback: (state: HAEntityState) => void) {
    this.subscribers.set(entityId, callback);
  }

  unsubscribe(entityId: string) {
    this.subscribers.delete(entityId);
  }

  disconnect() {
    this.ws?.close();
    this.ws = null;
    this.subscribers.clear();
  }
}
